import { ArrowRight, Code2, Bot, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatedGradientBackground } from "@/components/ui/animated-gradient-background";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { trackBookingClick } from "@/lib/analytics";
import TrustedBy from "@/components/ui/trusted-by";

const CALENDAR_URLS = {
  consultation: "https://cal.com/niumora-ai/project-consultation",
  bootcamp: "https://cal.com/niumora-ai/ai-bootcamp",
  expertSession: "https://cal.com/niumora-ai/expert-session",
} as const;

const Hero = () => {
  return (
    <AnimatedGradientBackground
      containerClassName="min-h-[800px] xl:min-h-[900px] 2xl:min-h-[1000px] max-h-[1200px] flex flex-col items-center justify-center pt-24"
      className="w-full"
      colors={[
        "rgba(56, 189, 248, 0.2)",  // Light blue
        "rgba(99, 102, 241, 0.25)", // Indigo
        "rgba(232, 121, 249, 0.2)", // Light pink
      ]}
    >
      <div className="container max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="space-y-8">
            <Badge variant="secondary" className="px-4 py-1.5 text-sm font-medium">
              <span className="flex h-2 w-2 rounded-full bg-primary mr-2"></span>
              AI-Powered Solutions
            </Badge>

            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display tracking-tight">
                <span className="block">
                  Transform Your Business
                </span>
                <span className="block">
                  With <span className="gradient-text">AI</span>
                </span>
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-xl">
                Feel behind on AI? We'll help you catch up fast—with custom tools and automations, hands-on training sessions, and our AI Bootcamp built for real people. No jargon, just clear wins. Learn how to use AI like the pros.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className={cn(
                  "rounded-full px-8 text-white shadow-lg hover:shadow-xl transition-all duration-300",
                  "gradient-button hover:scale-105"
                )}
                onClick={() => trackBookingClick('consultation', CALENDAR_URLS.consultation)}
              >
                Book Free Call <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="rounded-full px-8 hover:scale-105 transition-all duration-300"
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Learn More
              </Button>
            </div>
          </div>
          
          <div className="relative">
            <Card className="rounded-2xl bg-card/50 backdrop-blur-sm border shadow-2xl max-w-md mx-auto">
              <CardHeader>
                <Badge variant="default" className="w-fit">Our Services</Badge>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <Code2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Custom Software & Automations</h3>
                    <p className="text-sm text-muted-foreground">AI-powered solutions that work exactly how your business needs.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <Bot className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">AI Implementation</h3>
                    <p className="text-sm text-muted-foreground">Expert guidance to integrate AI into your business processes.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <Rocket className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">AI Bootcamp</h3>
                    <p className="text-sm text-muted-foreground">Master AI tools and strategies in our intensive training program.</p>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  className={cn(
                    "w-full rounded-full text-white shadow-lg hover:shadow-xl transition-all duration-300",
                    "gradient-button hover:scale-105"
                  )}
                  onClick={() => {
                    const servicesSection = document.getElementById('services');
                    if (servicesSection) {
                      servicesSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  View All Services <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>

      {/* Companies/Logos Section - Now using the TrustedBy component */}
      <TrustedBy className="mt-8" />
    </AnimatedGradientBackground>
  );
};

export default Hero;
